import { MutationResult, QueryResult, useQuery } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  SendPreviewMessagesMutation,
  SendPreviewMessagesMutationVariables,
  ListPreviewMessageContactsQuery,
  ListPreviewMessageContactsQueryVariables,
  SendPreviewMessageContactInput,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikTableSelectField from "~frontend/components/forms/FormikTableSelectField";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { GET_CAMPAIGN_PREVIEW_MESSAGES } from "~frontend/graphql/campaigns";
import {
  LIST_PREVIEW_MESSAGE_CONTACTS,
  SEND_PREVIEW_MESSAGES,
} from "~frontend/graphql/previewMessages";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import {
  cleanPhoneNumber,
  phoneNumberIsValid,
  prettyPhoneNumber,
} from "~frontend/util/phoneNumber";
import { spacingArray } from "~frontend/util/styling";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../campaignEditor/types";
import { SendDetailsMini } from "../CampaignDetails";
import { VariantOverview } from "../VariantOverview";
import { CampaignForView } from "../types";

function filterVariants(
  campaign: CampaignForView | CampaignForEditor,
  singleVariant?: string
) {
  if (singleVariant) {
    return campaign.scriptVariants.filter(v => v.id === singleVariant);
  } else {
    return campaign.scriptVariants.filter(v => v.deletedAt === null);
  }
}

function SendForm({
  campaign,
  listPreviewContactsResult,
  singleVariant,
  singleVariantIndex,
  sampleContactData,
}: {
  campaign: CampaignForView | CampaignForEditor;
  listPreviewContactsResult: QueryResult<
    ListPreviewMessageContactsQuery,
    ListPreviewMessageContactsQueryVariables
  >;
  singleVariant?: string;
  singleVariantIndex?: number;
  sampleContactData?: Record<string, string>;
}) {
  const { subaccountLink } = useSubaccount()!;

  const previewContacts =
    listPreviewContactsResult.data?.previewMessageContacts ?? [];
  let noPreviewContactsMessage = "No Preview Text Numbers Configured";
  if (listPreviewContactsResult.loading) {
    noPreviewContactsMessage = "Loading...";
  } else if (listPreviewContactsResult.error) {
    noPreviewContactsMessage = "Error loading preview text numbers";
  }

  const sampleContactDataFormatted =
    sampleContactData ||
    formatContactData({
      contactData: JSON.parse(campaign.sampleContact?.data ?? "{}"),
      campaignContactFields: campaign.contactFields,
    }).formattedData;

  return (
    <>
      <Typography>
        Select which numbers you want to send your preview text to. Preview text
        numbers can be added and edited on the{" "}
        <InternalLink
          to={subaccountLink("/settings")}
          sx={{ color: palette.navy }}
        >
          Settings Page of your subaccount
        </InternalLink>
        , or you can add new phone numbers just for this test in the field
        below. Each number will receive all script variants, and all custom
        fields will use your selected sample contact&apos;s information.
      </Typography>

      <FormikErrorBanner />

      <FormikTableSelectField
        name="contactIds"
        rows={previewContacts}
        columns={[
          {
            key: "name",
            title: "Name",
            renderValue: row => <span>{row.name}</span>,
          },
          {
            key: "phone",
            title: "Phone Number",
            renderValue: row => (
              <span>{prettyPhoneNumber(row.phoneNumber)}</span>
            ),
            sx: { width: "200px" },
          },
        ]}
        noFooter
        emptyState={noPreviewContactsMessage}
      />

      <FormikTextField
        sx={{ mt: 2 }}
        name="newNumbers"
        label="New Preview Text Numbers"
        bottomLabel="Enter a comma separated list of phone numbers. These numbers won’t be saved for future use."
        fullWidth
      />

      <Box
        style={{
          backgroundColor: palette.lightestGray,
          padding: spacingArray([1, 3, 3, 3]),
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <SendDetailsMini campaign={campaign} />

        <VariantOverview
          campaignId={campaign.id}
          scriptVariants={filterVariants(campaign, singleVariant)}
          sampleContactData={sampleContactDataFormatted}
          showFieldExplanation={false}
          scriptVariantsStartingIndex={singleVariantIndex}
        />
      </Box>
    </>
  );
}

function SendResults({
  nContacts,
  nVariants,
  sendPreviewMessagesState,
}: {
  sendPreviewMessagesState: MutationResult<SendPreviewMessagesMutation>;
  nContacts: number;
  nVariants: number;
}) {
  const getErrorMessage = useErrorMessageFormatter();

  if (sendPreviewMessagesState.error) {
    return (
      <Alert severity="error">
        {getErrorMessage(sendPreviewMessagesState.error)}
      </Alert>
    );
  }

  const data = sendPreviewMessagesState.data!.sendPreviewMessages;

  return (
    <Box sx={{ my: 3 }}>
      <Typography>
        Sent preview texts for{" "}
        {nVariants === 1 ? "1 variant" : `${nVariants} variants`} to{" "}
        {nContacts === 1 ? "1 number" : `${nContacts} numbers`}.
      </Typography>
      {data.errors.length > 0 && (
        <>
          <Typography>
            Sent {data.nSent === 1 ? "1 text" : `${data.nSent} texts`}{" "}
            successfully, but there were some errors:
          </Typography>
          <ul>
            {data.errors.map((error, i) => (
              <li key={i}>
                {error.previewMessageContactName
                  ? `${error.previewMessageContactName} - `
                  : ""}
                {prettyPhoneNumber(error.previewMessageContactPhoneNumber)}:{" "}
                {error.message}
              </li>
            ))}
          </ul>
        </>
      )}
    </Box>
  );
}

export const SendPreviewFormDialog = ({
  campaign,
  handleClose,
  revealSendStatus,
  singleVariant,
  singleVariantIndex = 1,
  sampleContactData,
}: {
  campaign: CampaignForView | CampaignForEditor;
  handleClose: () => void;
  revealSendStatus: () => void;
  singleVariant?: string;
  singleVariantIndex?: number;
  sampleContactData?: Record<string, string>;
}): ReactElement => {
  const { subaccount } = useSubaccount()!;

  const [sendPreviewMessages, sendPreviewMessagesState] = useMutation<
    SendPreviewMessagesMutation,
    SendPreviewMessagesMutationVariables
  >(SEND_PREVIEW_MESSAGES, {
    refetchQueries: [GET_CAMPAIGN_PREVIEW_MESSAGES],
    update: cache => {
      cache.modify({
        id: cache.identify(campaign),
        fields: {
          hasPreviewMessages: () => true,
        },
      });
    },
  });

  const listPreviewContactsResult = useQuery<
    ListPreviewMessageContactsQuery,
    ListPreviewMessageContactsQueryVariables
  >(LIST_PREVIEW_MESSAGE_CONTACTS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const handleCloseAndRevealSendStatus = () => {
    handleClose();
    revealSendStatus();
  };

  const [nContacts, setNContacts] = useState(0);

  let state: "initial" | "loading" | "done" = "initial";
  if (sendPreviewMessagesState.called) {
    state = sendPreviewMessagesState.loading ? "loading" : "done";
  }

  return (
    <Formik
      initialValues={{
        [submitErrorField]: "",
        contactIds: [] as string[],
        newNumbers: "",
      }}
      validationSchema={Yup.object().shape({
        newNumbers: Yup.string().test(
          "valid-phone-number-list",
          "Must be a valid phone number",
          function (value, context) {
            if (!value) {
              return true;
            }

            const numbers = value.split(",").map(n => n.trim());
            const invalidNumber = numbers.find(n => !phoneNumberIsValid(n));
            if (invalidNumber) {
              return this.createError({
                path: context.path,
                message: `"${invalidNumber}" is not a valid phone number`,
              });
            }

            return true;
          }
        ),
      })}
      onSubmit={(values, { setFieldError }) => {
        if (subaccount.sendingDisabled) {
          enqueueSnackbar({
            message:
              "Cannot send preview message because messaging is not enabled. Please reach out to support@scaletowin.com.",
          });
          return;
        }

        const contacts: SendPreviewMessageContactInput[] = [];

        // we filter previewMessageContacts by which ones are in contactIds,
        // and not vice-versa, to preserve the order of the selected contacts
        listPreviewContactsResult.data!.previewMessageContacts.forEach(
          contact => {
            if (values.contactIds.includes(contact.id)) {
              contacts.push({
                name: contact.name,
                phoneNumber: contact.phoneNumber,
              });
            }
          }
        );

        values.newNumbers.split(",").forEach(phoneNumber => {
          const trimmed = phoneNumber.trim();
          if (trimmed === "") {
            return;
          }

          const formatted = cleanPhoneNumber(trimmed);

          contacts.push({
            name: null,
            phoneNumber: formatted,
          });
        });

        if (contacts.length === 0) {
          setFieldError(
            submitErrorField,
            "You must select at least one number"
          );
          return;
        }

        setNContacts(contacts.length);

        void sendPreviewMessages({
          variables: {
            input: {
              campaignId: campaign.id,
              previewMessageContacts: contacts,
              scriptVariantId: singleVariant,
              sampleContactDataJSON: sampleContactData
                ? JSON.stringify(sampleContactData)
                : null,
            },
          },
        });
      }}
    >
      {({ isSubmitting, submitForm, values }) => (
        <Form>
          <Dialog
            open={true}
            onClose={!sendPreviewMessagesState.loading ? handleClose : () => {}}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              Send a Preview Text of {campaign.name}
              {singleVariantIndex && campaign.scriptVariants.length > 1
                ? `, Variant ${singleVariantIndex}`
                : ""}
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingBottom: theme.spacing(2),
              }}
            >
              <AsyncWrapper
                loading={listPreviewContactsResult.loading}
                errors={listPreviewContactsResult.error}
              >
                {subaccount.sendingDisabled && (
                  <Box
                    sx={{
                      padding: "10px",
                      backgroundColor: palette.lightOrange,
                      width: "100%",
                      mb: "10px",
                    }}
                  >
                    <strong>Warning:</strong> This account cannot send texts at
                    this time. Please reach out to{" "}
                    <a
                      style={{
                        color: palette.blue,
                        textDecorationColor: palette.blue,
                      }}
                      href="mailto:support@scaletowin.com"
                    >
                      support@scaletowin.com
                    </a>
                    .
                  </Box>
                )}
                {state === "initial" && (
                  <SendForm
                    campaign={campaign}
                    listPreviewContactsResult={listPreviewContactsResult}
                    singleVariant={singleVariant}
                    singleVariantIndex={singleVariantIndex}
                    sampleContactData={sampleContactData}
                  />
                )}
                {state === "loading" && <Loading />}
                {state === "done" && (
                  <SendResults
                    nVariants={filterVariants(campaign, singleVariant).length}
                    nContacts={nContacts}
                    sendPreviewMessagesState={sendPreviewMessagesState}
                  />
                )}
              </AsyncWrapper>
            </DialogContent>
            <DialogActions
              sx={{
                padding: spacingArray([0, 3, 3]),
              }}
            >
              {state === "initial" && (
                <>
                  <Button
                    variant="contained"
                    color="tertiary"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <FormikSubmitButton
                    disabled={
                      isSubmitting ||
                      subaccount.sendingDisabled ||
                      (values.contactIds.length === 0 &&
                        values.newNumbers.trim().length === 0)
                    }
                    variant="contained"
                    submitText="Send Preview Texts"
                    onClick={submitForm}
                  />
                </>
              )}

              {state === "done" && (
                <Button
                  variant="contained"
                  color="tertiary"
                  onClick={handleCloseAndRevealSendStatus}
                >
                  View Delivery Status
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};
